import { useQueryClient } from "@tanstack/react-query";
import { Eye, Share2 } from "lucide-react";
import { useEffect } from "react";

import { DOSSIER_READ_ONLY_EVENT, OWNER_ONLY, setOpenDossier, setShownDossier } from "../api/client";
import type { Exercise } from "../api/types";
import { Callout } from "../components/Callout";
import { Motion, Presence } from "../components/presence";
import { Button, IconButton } from "../components/ui";
import { t } from "../i18n/i18n";
import { useUi } from "../state/ui";
import { AccessBadges } from "./AccessBadge";
import {
  accountsItem,
  designateDossierReadOnly,
  isOwner,
  ownerName,
  readOnly,
  readOnlyText,
  READ_ONLY_TARGET,
  SHARE_TARGET,
  useDossierLock,
  useOpenDossier,
  useSharing,
} from "./access";
import { ShareDialog } from "./ShareDialog";

/** Lecture seule et partage du dossier ouvert (docs/17 §3.2, §8.2, §8.3, lot 5).
 *
 *  - `ReadOnlyText` : « Lecture seule : dossier de {nom}. Demandez-lui l'accès en modification si vous devez y
 *    écrire. »
 *  - `DossierSharingBar` : sous le nom du dossier, dans la barre latérale — pastilles, « Partager… » (le propriétaire)
 *    et le bandeau de lecture seule, cible désignée à chaque action bloquée ; une icône dans le rail.
 *  - `useDossierAccessHost` : monté une fois par la coque ; pose le dossier ouvert pour les requêtes du client
 *    (`base=`, `exercise=`), et traite les refus 403 `READ_ONLY` et `OWNER_ONLY` du serveur : avis la première fois,
 *    désignation du bandeau ensuite, liste des dossiers relue (le partage a pu changer). */

export { readOnlyText } from "./access";

/** Coque : dossier ouvert posé pour le client de l'API (pendant le rendu, avant les vues qui l'interrogent) ; refus des
 *  droits traités ; fenêtre « Partager » rendue. */
export function useDossierAccessHost(): void {
  const exercise = useOpenDossier();
  // Mode hérité : aucun paramètre ajouté, les requêtes restent celles d'avant ; seule la date de l'exercice (routes du
  // terrain) lit le dossier affiché dans tous les modes.
  setOpenDossier(exercise && accountsItem(exercise) ? exercise.id : null);
  setShownDossier(exercise?.id ?? null);
  const locked = readOnly(exercise);
  const owner = ownerName(exercise);
  useEffect(() => {
    useDossierLock.setState({ locked, ownerName: owner });
  }, [locked, owner]);
  const queryClient = useQueryClient();
  useEffect(() => {
    const onRefused = (event: Event) => {
      const detail = (event as CustomEvent<{ code?: string } | undefined>).detail;
      const exerciseId = useUi.getState().exerciseId;
      const current = queryClient.getQueryData<Exercise[]>(["exercises"])?.find((e) => e.id === exerciseId) ?? null;
      void queryClient.invalidateQueries({ queryKey: ["exercises"] });
      const notify = useUi.getState().notify;
      if (detail?.code === OWNER_ONLY) {
        notify({ text: t("Seul le propriétaire du dossier peut le supprimer, le partager ou en changer le propriétaire."), tone: "info" });
        return;
      }
      if (current && useSharing.getState().markExplained(current.id)) {
        notify({
          text: t("Ce dossier est en lecture seule pour vous."),
          detail: t("Demandez à {nom} l'accès en modification si vous devez y écrire.", { nom: ownerName(current) }),
          tone: "info",
        });
      }
      window.requestAnimationFrame(designateDossierReadOnly);
    };
    window.addEventListener(DOSSIER_READ_ONLY_EVENT, onRefused);
    return () => window.removeEventListener(DOSSIER_READ_ONLY_EVENT, onRefused);
  }, [queryClient]);
}

/** Fenêtre « Partager », ouverte par `useSharing.open(dossier)` depuis l'accueil, la barre latérale ou la DIREX.
 *  `inactive` : l'accueil est ouvert et rend la sienne, dans son plan (au-dessus de l'espace de travail). */
export function ShareDialogHost({ inactive = false }: { inactive?: boolean }) {
  const dialogFor = useSharing((s) => (inactive ? null : s.dialogFor));
  const close = useSharing((s) => s.close);
  return (
    <Presence show={Boolean(dialogFor)}>
      {dialogFor && <ShareDialog exerciseId={dialogFor.id} name={dialogFor.name} onClose={close} />}
    </Presence>
  );
}

/** Bandeau de lecture seule dans une fenêtre d'écriture du dossier (note 3A, ordre reçu, rotation…) : même texte et
 *  même cible que celui de la barre latérale, désigné à la place de celui-ci tant que la fenêtre est ouverte. Rien si le
 *  compte peut modifier le dossier. */
export function DossierReadOnlyNote({ className }: { className?: string }) {
  const exercise = useOpenDossier();
  const locked = readOnly(exercise);
  return (
    <Presence show={locked}>
      {locked && (
        <Callout tone="warn" icon={Eye} className={className}>
          <span data-attention={READ_ONLY_TARGET} className="block rounded-md">
            {readOnlyText(ownerName(exercise))}
          </span>
        </Callout>
      )}
    </Presence>
  );
}

/** Barre latérale, sous le nom du dossier ouvert : pastilles, « Partager… », bandeau de lecture seule. */
export function DossierSharingBar({ collapsed }: { collapsed: boolean }) {
  const exercise = useOpenDossier();
  const open = useSharing((s) => s.open);
  const shown = Boolean(exercise && exercise.access);
  const locked = readOnly(exercise);
  return (
    <Presence show={shown}>
      {shown && exercise && (
        <Motion origin="fade" className="space-y-1.5 px-2 pt-1 pb-1.5">
          {collapsed ? (
            locked && (
              <div className="flex justify-center">
                <IconButton
                  size="sm"
                  data-attention={READ_ONLY_TARGET}
                  title={readOnlyText(ownerName(exercise))}
                  aria-label={readOnlyText(ownerName(exercise))}
                  onClick={designateDossierReadOnly}
                >
                  <Eye size={16} className="text-warn" />
                </IconButton>
              </div>
            )
          ) : (
            <>
              <div className="flex min-h-8 flex-wrap items-center gap-1.5">
                <AccessBadges ex={exercise} />
                <span className="flex-1" />
                <Button
                  variant="link"
                  data-attention={SHARE_TARGET}
                  onClick={() => open(exercise)}
                  title={isOwner(exercise) ? undefined : t("Voir le partage de ce dossier")}
                >
                  <Share2 size={13} /> {t("Partager…")}
                </Button>
              </div>
              <Presence show={locked}>
                {locked && (
                  <Callout tone="warn" icon={Eye}>
                    <span data-attention={READ_ONLY_TARGET} className="block rounded-md">
                      {readOnlyText(ownerName(exercise))}
                    </span>
                  </Callout>
                )}
              </Presence>
            </>
          )}
        </Motion>
      )}
    </Presence>
  );
}
